import { JcDTO } from './JcDTO';
import { JcMergeData, JcWriteData } from './JcWriteValue';

/**
 * Firebase-free handle to a single document, identified by its collection
 * `path` and document `id`. Replaces direct use of Firestore's
 * `DocumentReference` so that domain services can hand references to a
 * {@link JcTransaction} without importing Firebase. {@link BaseFirestoreService}
 * resolves it to a native reference when a transaction runs.
 *
 * Carries its DTO as a type parameter so `get`/`set`/`update` on a
 * {@link JcTransaction} or {@link JcBatch} are checked against the right
 * shape with no per-call type argument — construct with
 * `new JcDocRef<SomeDTO>(path, id)` once and every use of that ref is typed.
 * The parameter is phantom (never read or assigned) — `declare` keeps it out
 * of the emitted JS — but a private field's type is still part of a class's
 * structural identity, so `JcDocRef<A>` and `JcDocRef<B>` stay distinct types
 * even though neither actually stores `A`/`B` at runtime.
 */
export class JcDocRef<D extends JcDTO = JcDTO> {
    declare private readonly _dtoType?: D;

    constructor(
        readonly path: string,
        readonly id: string
    ) {}
}

/**
 * A document snapshot read inside a transaction. `data()` returns the document
 * body mapped to Firebase-free read values; `D` is asserted at the read
 * boundary (the document is schemaless), like every other typed read.
 */
export interface JcDocSnapshot<D extends JcDTO = JcDTO> {
    exists(): boolean;
    /** The document's data, or `undefined` when it doesn't exist. */
    data(): D | undefined;
}

/**
 * Firebase-free transaction handle passed to {@link BaseFirestoreService.transaction}.
 * Mirrors Firestore's transaction API but operates on {@link JcDocRef}s and
 * {@link JcWriteData}/{@link JcMergeData} payloads (which may contain `JcData*`
 * values and write sentinels — the base service maps them to native Firestore
 * values). `D` is inferred from the ref, so callers never name it explicitly.
 */
export interface JcTransaction {
    /** Reads a document inside the transaction. */
    get<D extends JcDTO>(ref: JcDocRef<D>): Promise<JcDocSnapshot<D>>;
    /** Creates or overwrites a document — the full DTO shape, sentinels allowed. */
    set<D extends JcDTO>(ref: JcDocRef<D>, data: JcWriteData<D>): void;
    /**
     * Creates the document if absent, or deep-merges the given fields into it
     * if it exists — recursively, at every depth, so a nested DTO field may be
     * given only in part and its other fields survive untouched. The only safe
     * way to touch one field of a nested DTO (e.g. a score) without wiping its
     * siblings: a transform sentinel (`JcIncrement`, `JC_SERVER_TIMESTAMP`)
     * nested under a field given to plain {@link update} is applied *after*
     * that field's wholesale replacement — silently discarding whatever it was
     * meant to increment/stamp from. Reach for `update` only when every nested
     * field you're touching is fully specified in the call.
     */
    merge<D extends JcDTO>(ref: JcDocRef<D>, data: JcMergeData<D>): void;
    /** Patches an existing document. Nested DTO fields replace wholesale — see {@link merge} for the alternative. */
    update<D extends JcDTO>(ref: JcDocRef<D>, data: Partial<JcWriteData<D>>): void;
    /** Deletes a document. */
    delete(ref: JcDocRef): void;
}

/**
 * Firebase-free handle to an atomic, unconditional multi-document write,
 * passed to {@link BaseFirestoreService.batch}. Unlike {@link JcTransaction},
 * it has no `get` — it never reads, so (per Firestore's own guidance) prefer
 * this over a transaction whenever the write doesn't depend on a prior read.
 * The upside: unlike a transaction (which requires a server round-trip before
 * it can be reflected anywhere, since its outcome depends on read-time
 * version checks), a batch is applied to the local cache immediately, so
 * every listener sees the change instantly with no extra client bookkeeping.
 */
export interface JcBatch {
    /** Creates or overwrites a document — the full DTO shape, sentinels allowed. */
    set<D extends JcDTO>(ref: JcDocRef<D>, data: JcWriteData<D>): void;
    /** Creates the document if absent, or deep-merges the given fields if it exists — see {@link JcTransaction.merge}. */
    merge<D extends JcDTO>(ref: JcDocRef<D>, data: JcMergeData<D>): void;
    /** Patches an existing document. Nested DTO fields replace wholesale — see {@link merge} for the alternative. */
    update<D extends JcDTO>(ref: JcDocRef<D>, data: Partial<JcWriteData<D>>): void;
    /** Deletes a document. */
    delete(ref: JcDocRef): void;
}
